import { useState } from "react";
import { ChevronRight, Circle } from "lucide-react";
import DOMPurify from "dompurify";
import { marked } from "marked";
import StickyIssueHeader from "../../common/StickyIssueHeader";
import {
  issueCardBodyClass,
  issueCardCollapsedClass,
  issueCardOpenShellClass,
} from "../../../utils/stickyIssueHeader";

function AuditListSection({
  title,
  audits,
  knownIssues,
  tone = "failed",
  defaultVisible = true,
  collapsible = false,
}) {
  const [visible, setVisible] = useState(defaultVisible);
  const [openAudits, setOpenAudits] = useState({});

  if (!audits.length) return null;

  const toggleAudit = (id) => {
    setOpenAudits((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toneClasses =
    tone === "passed"
      ? {
          title: "text-green-700",
          icon: "text-green-600 fill-current",
        }
      : {
          title: "text-red-700",
          icon: "text-red-500 fill-current",
        };

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between border-t border-border pt-4 mb-3">
        <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          {title} ({audits.length})
        </p>
        {collapsible && (
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            className="text-sm text-brand hover:underline"
          >
            {visible ? "Hide" : "Show"}
          </button>
        )}
      </div>

      {visible && (
        <div className="space-y-3">
          {audits.map((audit) => {
            const issue = knownIssues?.[audit.id];
            const isExpandable =
              tone === "failed" &&
              (issue?.description ||
                (issue?.items && issue.items.length > 0));
            const isOpen = openAudits[audit.id] && isExpandable;
            const passLabel =
              tone === "passed" ? " — All audits passed" : "";

            return (
              <div
                key={audit.id}
                data-issue-card
                className={
                  isOpen ? issueCardOpenShellClass : issueCardCollapsedClass
                }
              >
                <StickyIssueHeader
                  active={isOpen}
                  className={`flex items-start p-4 ${
                    isExpandable ? "cursor-pointer" : ""
                  }`}
                  onClick={() => isExpandable && toggleAudit(audit.id)}
                >
                  <Circle
                    size={12}
                    className={`mr-3 mt-1 shrink-0 ${toneClasses.icon}`}
                  />
                  <div className="flex-grow flex justify-between items-center min-w-0 gap-x-4">
                    <span className={`font-medium ${toneClasses.title}`}>
                      {audit.title}
                      {passLabel}
                    </span>
                    {issue?.display && (
                      <span className="text-sm text-muted-foreground font-medium text-right shrink-0 leading-tight">
                        {issue.display}
                      </span>
                    )}
                  </div>
                  {isExpandable && (
                    <ChevronRight
                      size={20}
                      className={`ml-2 text-muted-foreground shrink-0 transition-transform mt-0.5 ${
                        openAudits[audit.id] ? "rotate-90" : ""
                      }`}
                    />
                  )}
                </StickyIssueHeader>

                {isOpen && issue && (
                  <div className={issueCardBodyClass}>
                    {issue.description && (
                      <div
                        className="prose prose-sm max-w-none text-foreground diagnostic-description"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            marked.parse(issue.description),
                          ),
                        }}
                      />
                    )}
                    {issue.items?.length > 0 && issue.headings && (
                      <div className="overflow-x-auto mt-4">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="text-left text-muted-foreground">
                              {issue.headings.map((heading) => (
                                <th
                                  key={heading.key}
                                  className="py-2 px-4 font-semibold"
                                >
                                  {heading.label || heading.text}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {issue.items.map((item, index) => (
                              <tr key={index}>
                                {issue.headings.map((heading) => (
                                  <td
                                    key={`${index}-${heading.key}`}
                                    className="py-2 px-4 align-top"
                                  >
                                    {typeof item[heading.key] === "object"
                                      ? JSON.stringify(item[heading.key])
                                      : item[heading.key]}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const AgenticBrowsingDetails = ({ agenticBrowsing, knownIssues }) => {
  if (!agenticBrowsing?.totalCount) return null;

  const failedAudits = (agenticBrowsing.audits || []).filter(
    (a) => a.status === "failed",
  );
  const passedAudits = (agenticBrowsing.audits || []).filter(
    (a) => a.status === "passed",
  );
  const allPass =
    agenticBrowsing.passCount === agenticBrowsing.totalCount &&
    agenticBrowsing.totalCount > 0;

  return (
    <section className="mt-2">
      <div className="flex flex-col items-center text-center px-2 sm:px-6">
        <div
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 mb-4 ${
            allPass ? "bg-green-100" : "bg-amber-100"
          }`}
        >
          <span
            className={`h-2 w-2 rounded-full ${
              allPass ? "bg-green-600" : "bg-amber-600"
            }`}
          />
          <span
            className={`text-base font-semibold tabular-nums ${
              allPass ? "text-green-800" : "text-amber-800"
            }`}
          >
            {agenticBrowsing.passRatio}
          </span>
        </div>

        <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-3">
          {agenticBrowsing.title}
        </h2>

        {agenticBrowsing.description && (
          <div
            className="prose prose-sm max-w-2xl text-muted-foreground diagnostic-description"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                marked.parse(agenticBrowsing.description),
              ),
            }}
          />
        )}
      </div>

      {failedAudits.length > 0 && (
        <AuditListSection
          title="Failed audits"
          audits={failedAudits}
          knownIssues={knownIssues}
          tone="failed"
        />
      )}

      <AuditListSection
        title="Passed audits"
        audits={passedAudits}
        knownIssues={knownIssues}
        tone="passed"
        collapsible
        defaultVisible
      />
    </section>
  );
};

export default AgenticBrowsingDetails;
